import { useState, useEffect, useCallback } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useMutation } from "convex/react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod/v4";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { useProject } from "@/components/projects";
import { PageHeader } from "@/components/layout";
import {
  ProjectDocumentPicker,
  type PickerDocument,
  type PickerSection,
  type SectionSelections,
} from "@/components/project-document-picker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useAppNavigate } from "@/lib/use-app-navigate";

export const Route = createFileRoute("/specPilot/new")({
  component: NewSubmittalPage,
});

const formSchema = z.object({
  name: z.string().min(1, "SpecPilot job name is required"),
});

type FormValues = z.infer<typeof formSchema>;

export function NewSubmittalPage() {
  const navigate = useAppNavigate();
  const { projectId, project, isLoading: isProjectLoading } = useProject();

  const createJob = useMutation(api.specPilot.submittals.createFromRevision);

  const [pickerValues, setPickerValues] = useState<
    Record<string, SectionSelections>
  >({ schedules: { mode: "single", selection: null } });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasUserEditedName, setHasUserEditedName] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema as never),
    defaultValues: { name: "" },
  });

  const { data: scheduleOptionsRaw } = useQuery(
    convexQuery(
      api.projectDocumentRevisionOptions.listClassifiedPdfRevisionOptions,
      projectId ? { projectId, documentType: "schedule" } : "skip"
    )
  );

  const scheduleDocs: PickerDocument[] = (scheduleOptionsRaw ?? []).map(
    (opt) => ({
      _id: String(opt._id),
      name: opt.name,
      revisions: opt.revisions.map((r) => ({
        _id: String(r._id),
        revisionNumber: r.revisionNumber,
        revisionName: r.revisionName,
      })),
    })
  );

  const sections: PickerSection[] = [
    {
      key: "schedules",
      label: "Schedules",
      documents: scheduleDocs,
      selectionMode: "single",
      revisionMode: "single",
    },
  ];

  useEffect(() => {
    if (!isProjectLoading && !projectId) {
      navigate({ to: "/projects", title: "Projects" });
    }
  }, [isProjectLoading, projectId, navigate]);

  useEffect(() => {
    setPickerValues({ schedules: { mode: "single", selection: null } });
    setHasUserEditedName(false);
  }, [projectId]);

  // Auto-populate name when a revision is selected
  const selectedRevisionId =
    pickerValues.schedules?.mode === "single"
      ? pickerValues.schedules.selection?.revisionId
      : undefined;
  const selectedDocumentId =
    pickerValues.schedules?.mode === "single"
      ? pickerValues.schedules.selection?.documentId
      : undefined;

  useEffect(() => {
    if (!selectedRevisionId || !selectedDocumentId) return;
    const doc = scheduleOptionsRaw?.find(
      (o) => String(o._id) === selectedDocumentId
    );
    const rev = doc?.revisions.find(
      (r) => String(r._id) === selectedRevisionId
    );
    if (rev && !hasUserEditedName) {
      form.setValue("name", rev.originalFilename.replace(/\.pdf$/i, ""), {
        shouldValidate: true,
      });
    }
  }, [selectedRevisionId, selectedDocumentId, scheduleOptionsRaw, form, hasUserEditedName]);

  const handlePickerChange = useCallback(
    (sectionKey: string, value: SectionSelections) => {
      setPickerValues((prev) => ({ ...prev, [sectionKey]: value }));
    },
    []
  );

  const onSubmit = async (values: FormValues) => {
    if (!projectId) {
      toast.error("Please select a project first");
      return;
    }
    if (!selectedRevisionId) {
      toast.error("Select a schedule document and revision");
      return;
    }

    setIsSubmitting(true);
    try {
      const jobId = await createJob({
        projectId,
        revisionId: selectedRevisionId as Id<"projectDocumentRevisions">,
        name: values.name,
      });
      toast.success("SpecPilot job created and processing started");
      void navigate({ to: "/specPilot/$jobId", params: { jobId } });
    } catch (error) {
      console.error("Submission error:", error);
      toast.error("Failed to create SpecPilot job. Please try again.");
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="New SpecPilot Job"
        description="Select a schedule from project documents to create a SpecPilot job"
        breadcrumbs={[
          { label: "SpecPilot", href: "/specPilot" },
          { label: "New SpecPilot Job" },
        ]}
      />

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle>Create SpecPilot Job</CardTitle>
          <CardDescription>
            {project ? (
              <>
                Creating in project: <strong>{project.name}</strong>. Select a
                classified schedule from the project library. We will extract the
                tables for this SpecPilot job.
              </>
            ) : (
              <>
                Select a classified schedule from the project library. We will
                extract the tables for this SpecPilot job.
              </>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>SpecPilot Job Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter SpecPilot job name"
                        name={field.name}
                        ref={field.ref}
                        value={field.value}
                        onBlur={field.onBlur}
                        onChange={(event) => {
                          setHasUserEditedName(true);
                          field.onChange(event);
                        }}
                        disabled={isSubmitting}
                      />
                    </FormControl>
                    <FormDescription>
                      Auto-populated when you select a revision.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <ProjectDocumentPicker
                sections={sections}
                values={pickerValues}
                onChange={handlePickerChange}
                isLoading={scheduleOptionsRaw === undefined}
                disabled={isSubmitting}
              />

              <div className="flex items-center justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  disabled={isSubmitting}
                  onClick={() =>
                    void navigate({ to: "/specPilot", title: "SpecPilot" })
                  }
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Loader2 className="size-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    "Create SpecPilot Job"
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
